import { useCallback, useEffect, useState } from 'react'

import { useConnect } from 'redux-bundler-hook'

import { Close } from '@mui/icons-material'
import { Box, Button, Dialog, DialogContent, IconButton } from '@mui/material'

import { Loading } from '@common/components'

import { GENERIC_PDF_EXPORT_ERROR } from '../reportExportBundle'
import SmokeQualityReportContent from './SmokeQualityReportContent'

/**
 * @component
 * @param {Object} props - The props for the component.
 * @param {Object} [props.event]
 * @param {string} props.event.id
 * @param {string} props.event.createdOn
 * @param {string} props.event.propertyName
 * @param {string} props.event.unitName
 * @param {number} props.event.childCount
 * @param {Object} [props.event.reservation]
 * @param {string} props.event.reservation.id
 * @param {string} props.event.reservation.confirmationNumber
 * @param {string} props.event.timeZone
 * @param {number} [props.event.eventPosition]
 * @param {Function} props.onClose
 */
export default function SmokeQualityReport({ event = undefined, onClose }) {
  const [initialLoading, setInitialLoading] = useState(true)

  const {
    dataQualityReportData,
    dataQualityReportDataError,
    dataQualityReportDataIsLoading,
    dataQualityReportExportIsLoading,
    doFetchDataQualityReportData,
    doResetReportDataState,
    doExportDataQualityReport,
    doShowSnackbar,
  } = useConnect(
    'selectDataQualityReportData',
    'selectDataQualityReportDataError',
    'selectDataQualityReportDataIsLoading',
    'selectDataQualityReportExportIsLoading',
    'doFetchDataQualityReportData',
    'doResetReportDataState',
    'doExportDataQualityReport',
    'doShowSnackbar',
  )

  const fetchReport = useCallback(async () => {
    try {
      await doFetchDataQualityReportData(event.id)
    } catch (err) {
      // no need to show snackbar or smth. We use `eventReportError` to handle error
    } finally {
      setInitialLoading(false)
    }
  }, [event])

  useEffect(() => {
    if (event) {
      fetchReport()
    } else {
      doResetReportDataState()
      setInitialLoading(true)
    }
  }, [event])

  const downloadReportPDF = useCallback(async () => {
    try {
      await doExportDataQualityReport(event.id)
    } catch (error) {
      doShowSnackbar(GENERIC_PDF_EXPORT_ERROR, 'error')
    }
  }, [event])

  return (
    <Dialog
      data-testid="smoke_quality_report_dialog"
      open={!!event}
      fullScreen
      onClose={onClose}
    >
      <DialogContent sx={{ position: 'relative' }}>
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            top: 25,
            right: 25,
            zIndex: 1001,
          }}
        >
          <Close />
        </IconButton>

        <Button
          disabled={
            dataQualityReportDataIsLoading ||
            !dataQualityReportData ||
            dataQualityReportExportIsLoading
          }
          variant="contained"
          sx={{
            position: 'absolute',
            top: 25,
            right: 75,
            zIndex: 1001,
          }}
          onClick={downloadReportPDF}
        >
          Generate PDF
          {dataQualityReportExportIsLoading && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                maxHeight: 20,
                paddingLeft: 2,
              }}
            >
              <Loading size={10} />
            </Box>
          )}
        </Button>

        <Box style={{ margin: 10, marginLeft: 30, marginTop: 75 }}>
          <SmokeQualityReportContent
            data={dataQualityReportData}
            smokeQualityReportError={dataQualityReportDataError}
            smokeQualityReportIsLoading={dataQualityReportDataIsLoading}
            initialLoading={initialLoading}
          />
        </Box>
      </DialogContent>
    </Dialog>
  )
}
